import { useState } from 'react';
import { IconButton, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { useTrans } from '@libs/i18n';
import { CheckIcon, CopyIcon } from '@phosphor-icons/react';

interface CopyButtonProps {
  textToCopy: string;
}

export const CopyButton = observer(({ textToCopy }: CopyButtonProps) => {
  const { t } = useTrans();
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 3000);
    } catch {
      enqueueSnackbar(t('components.copyButton.failedToCopy'), { variant: 'error' });
    }
  };

  return (
    <IconButton
      onClick={handleClick}
      aria-label={t('components.copyButton.copy')}
      sx={{
        backgroundColor: copied ? theme.palette.black[900] : '#F5F5F5',
        borderRadius: '10px',
        color: copied ? theme.palette.common.white : theme.palette.text.secondary,
        width: 32,
        height: 32,
        transition: 'background-color .16s ease, color .16s ease, transform .12s ease',
        '&:hover': {
          backgroundColor: copied ? theme.palette.black[900] : '#EAEAEA',
          color: copied ? theme.palette.common.white : theme.palette.text.primary,
        },
        '&:active': {
          transform: copied ? 'scale(1)' : 'scale(0.9)',
        },
      }}
    >
      {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
    </IconButton>
  );
});
